/*
 * Copyright (c) by INFODATA-GROUP. All rights reserved.
 * No part of this publication may be reproduced, distributed,
 * or transmitted in any form or by any means, including photocopying, recording,
 * or other electronic or mechanical methods, without the prior written permission of the publisher,
 * except in the case of brief quotations embodied in critical
 * reviews and certain other noncommercial uses permitted by copyright law.
 *  author : vhe
 *  date : 02/03/2021 07:39
 */

import {
  AfterViewInit,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  output,
  QueryList,
  signal,
  ViewChild,
  viewChildren,
  ViewChildren,
  ChangeDetectionStrategy
} from '@angular/core';
import {
  CalendarClickEvent,
  CalendarDropTask,
  CalendarTask,
  CalendarTaskHeaderGroup,
  CommonUtils,
  MenuDisplayService,
  Time,
  WeekSelectionEvent,
} from '@infodata-group/inf-commons';
import { CalendarView } from '../calendar-view';
import { CalendarViewDayByHourHorizontalComponent } from '../calendar-view-day-by-hour-horizontal/calendar-view-day-by-hour-horizontal.component';
import jsPDF from 'jspdf';
import { CalendarHorizontalResourcesComponent } from '../calendar-horizontal-resources/calendar-horizontal-resources.component';
import { CalendarViewDayHorizontalComponent } from '../calendar-view-day-horizontal/calendar-view-day-horizontal.component';
import { CalendarResourceLine } from '../calendar-resource-line';
import { Telework } from '../telework';

@Component({
  selector: 'infl-calendar-view-week-horizontal',
  templateUrl: './calendar-view-week-horizontal.component.html',
  styleUrls: ['./calendar-view-week-horizontal.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CalendarHorizontalResourcesComponent,
    CalendarViewDayByHourHorizontalComponent,
    CalendarViewDayHorizontalComponent,
  ],
})
export class CalendarViewWeekHorizontalComponent
  implements CalendarView, AfterViewInit
{
  public resources = input<CalendarTaskHeaderGroup[]>();
  public partial = input(false);
  public start = input<Time>({ hours: 7, minutes: 0 });
  public endMorning = input<Time>({ hours: 12, minutes: 0 });
  public startAfternoon = input<Time>({ hours: 13, minutes: 0 });
  public end = input<Time>({ hours: 17, minutes: 30 });
  public resourceTitle = input<string>();
  public days = input<{
    monday: boolean;
    tuesday: boolean;
    wednesday: boolean;
    thursday: boolean;
    friday: boolean;
    saturday: boolean;
    sunday: boolean;
  }>();
  public hourDivCount = input(2);
  public day = input(new Date());
  public dragByOutside = input<CalendarDropTask<any>>();
  public tasks = input<CalendarTask[]>();
  public sectionHeight = input(40);
  public taskHeight = input(30);
  public canDrag = input(true);
  public canResize = input(true);
  public canSelect = input(true);
  public enable = input(true);
  public cellWidth = input<number | 'auto'>('auto');
  public showResources = input(false);
  public numberSection = input<1 | 2>();
  public onFortnight = input<boolean>(false);
  public draggingTask = input<{ task: CalendarTask; shift: number }>();
  public teleworks = input<Telework[]>(null);

  public periodSelection = output<WeekSelectionEvent>();
  public hourPreselection = output<WeekSelectionEvent>();
  public taskUpdate = output<CalendarTask>();
  public taskCreation = output<{
    date: Date;
    group: { header: string; group: string };
  }>();
  public taskClick = output<CalendarClickEvent>();
  public taskHover = output<CalendarTask>();
  public taskDblClick = output<CalendarClickEvent>();
  public dropOutside = output<void>();
  public taskContextMenu = output<CalendarTask>();
  public clickOnSelection = output<WeekSelectionEvent>();
  public dblClickOnSelection = output<WeekSelectionEvent>();
  public resourcesInit = output<CalendarResourceLine[]>();
  public startDraggingTask = output<{
    task: CalendarTask;
    shift: number;
  }>();

  @ViewChildren('days')
  dayViews: QueryList<
    | CalendarViewDayByHourHorizontalComponent
    | CalendarViewDayHorizontalComponent
  >;
  protected daysContent = viewChildren<ElementRef>('daysContent');
  @ViewChild('resourcesComponent')
  resourcesComponent: CalendarHorizontalResourcesComponent;

  public resourceLines: CalendarResourceLine[];
  public week: {
    date: Date;
    timeStamp: number;
    isToday: boolean;
  }[];
  public selectedView: CalendarView;

  public weekNumber = computed(() => {
    return this.dateWeek(this.day());
  });

  public sectionWidth = signal(0);

  public weekContainerStyle = computed(() => {
    if (this.showResources()) {
      return 'calc(100% - 230px)';
    }
    return '100%';
  });

  private menuService = inject(MenuDisplayService);

  constructor() {
    effect(() => {
      if (this.resources() && this.day()) {
        this.createDays();
      }
      if (this.days() || this.onFortnight()) {
        this.sectionWidth.set(this.computeSectionWidth());
      }
    });
    this.menuService.subscribe(() => {
      setTimeout(() => {
        this.sectionWidth.set(this.computeSectionWidth());
      }, 500);
    });
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.scrollToToday();
    }, 850);
    this.sectionWidth.set(this.computeSectionWidth());
  }

  private computeSectionWidth(): number {
    if (!this.days()) {
      return 0;
    }
    const keys = Object.keys(this.days());
    let nbDays = 0;
    keys.forEach((key) => {
      if (this.days()[key]) {
        nbDays++;
      }
    });
    if (this.onFortnight()) {
      nbDays *= 2;
    }
    const container = document.getElementsByClassName('calendar-container')[0];
    if (!container) {
      return 120;
    }
    const containerWidth = container.clientWidth - 230;
    const additionalBorder = this.showResources() ? 0 : 1;
    if (120 * nbDays > containerWidth) {
      return 120;
    }
    return (containerWidth - nbDays - additionalBorder) / nbDays;
  }

  private createDays() {
    this.resourceLines = [];
    const start = CommonUtils.startOfTheWeek(this.day());
    this.week = [];
    let i = 0;
    for (const d in this.days()) {
      if (this.days()[d]) {
        const date = CommonUtils.addDayToDate(start, i);
        this.week.push({
          date: date,
          timeStamp: date.getTime(),
          isToday: CommonUtils.isToday(date),
        });
      }
      ++i;
    }
    for (const header of this.resources()) {
      for (const resource of header.tasksGroups) {
        this.resourceLines.push(
          new CalendarResourceLine(
            header,
            resource,
            this.week.map((w) => w.date),
            this.sectionHeight(),
          ),
        );
      }
    }
    this.resourcesInit.emit(this.resourceLines);
  }

  onDragTask(event: { task: CalendarTask; shift: number }) {
    this.startDraggingTask.emit(event);
  }

  deselect(): void {
    if (this.selectedView) {
      this.selectedView.deselect();
      this.selectedView = null;
    }
  }

  onRightClick(event: CalendarTask) {
    this.taskContextMenu.emit(event);
  }

  onHourSelection(
    view:
      | CalendarViewDayByHourHorizontalComponent
      | CalendarViewDayHorizontalComponent,
    event: WeekSelectionEvent,
  ) {
    if (this.selectedView && this.selectedView !== view) {
      this.selectedView.deselect();
    }
    this.selectedView = view;
    this.periodSelection.emit(event);
  }

  onChangeDisplay() {
    this.dayViews.forEach((day) => {
      day.generateTasks();
    });
  }

  public generateTasks() {
    this.onChangeDisplay();
  }

  onClickOnSelection(
    day: Date,
    selection: WeekSelectionEvent,
    dblClick?: boolean,
  ) {
    if (dblClick) {
      this.dblClickOnSelection.emit(selection);
    } else {
      this.clickOnSelection.emit(selection);
    }
  }

  findTask(id: string): { style: any; task: CalendarTask } {
    return null;
  }

  getScrollPosition(): { x: number; y: number } {
    return { x: 0, y: 0 };
  }

  setScrollPosition(x: number, y: number) {}

  public scrollToDate(date: Date): void {}

  private scrollToToday() {
    const container = document.querySelector(
      '.calendar-container',
    ) as HTMLElement;
    if (!container) {
      return;
    }

    const sections = Array.from(
      container.getElementsByClassName('section-day-hor'),
    ) as HTMLElement[];
    const currentSection = sections.find((section) =>
      section.classList.contains('current'),
    );
    if (!currentSection) {
      container.scrollTo({ top: 0, behavior: 'instant' });
      return;
    }

    const resizeObserver = new ResizeObserver(() => {
      let width = 0;
      for (const section of sections) {
        if (section === currentSection) {
          break;
        }
        width += section.clientWidth;
      }
      container.scrollTo({ left: width, behavior: 'instant' });
      resizeObserver.disconnect();
    });

    resizeObserver.observe(currentSection);
  }

  public toPdf(
    doc: jsPDF,
    width: number,
    x: number,
    height?: number,
    last?: boolean,
  ) {
    const lastPageHeight = this.resourcesComponent.toPdf(doc);
    const dayWidth = (doc.internal.pageSize.width - 250) / this.week.length;
    x = 230;
    for (const day of this.dayViews.toArray()) {
      day.toPdf(doc, dayWidth, x, lastPageHeight, true);
      x += dayWidth;
    }
  }

  public dateWeek(date: Date): string {
    const tempDate = new Date(
      Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()),
    );
    const day = tempDate.getUTCDay() || 7;
    tempDate.setUTCDate(tempDate.getUTCDate() + 4 - day);
    const yearStart = new Date(Date.UTC(tempDate.getUTCFullYear(), 0, 1));
    const weekNumber = Math.ceil(
      ((tempDate.getTime() - yearStart.getTime()) / 86400000 + 1) / 7,
    );
    return weekNumber < 10 ? '0' + weekNumber : '' + weekNumber;
  }
}
